/**
 * UFT Failure Reporter
 *
 * Shared helper for first-party scripts to report genuine, actionable failures
 * (a dependency that never loaded, a third-party hook that never appeared, an
 * a11y feature that silently didn't apply) to Sentry. It is:
 *   - tiered ....... level 'error' (default) for a broken feature, 'warning'
 *                    for a degraded-but-still-functional state
 *   - guarded ...... falls back to the console when Sentry isn't on the page, so
 *                    a failure is never 100% dark (e.g. brands with no Sentry id)
 *   - held ......... a report made before Sentry inits is logged, then queued
 *                    through the loader's stub or held and sent by flush()
 *   - throttled .... each fingerprint is reported at most once per page load
 *   - fingerprinted  so duplicates group together instead of flooding alerting
 *
 * Only call this on a real "this should have worked" failure. Stay silent on
 * expected absences (e.g. UpCart simply not installed on a given page) so this
 * never becomes noise.
 *
 * Usage: window.UFT.FailureReporter.report(fingerprint, message, { level, extra })
 *        window.UFT.FailureReporter.flush() (called by 3p-sentry.liquid after Sentry.init)
 */

window.UFT = window.UFT || {};
window.UFT.FailureReporter =
	window.UFT.FailureReporter ||
	(function () {
		const reported = new Set();
		const held = [];
		const DEFAULT_LEVEL = 'error';

		function report(fingerprint, message, options) {
			if (reported.has(fingerprint)) {
				return;
			}
			reported.add(fingerprint);

			const failure = {
				fingerprint,
				message,
				level: options?.level || DEFAULT_LEVEL,
				extra: options?.extra || {},
			};

			if (isSentryReady()) {
				send(failure);
				return;
			}

			// Never let a genuine failure go fully dark: log it now, because a Sentry
			// load that is blocked (ad blockers) never delivers what is queued or held.
			logToConsole(failure);
			// The loader replays its queue after init, and in lazy mode a queued
			// capture is what makes it fetch the SDK at all, so a hold would never flush.
			if (isLoaderQueueing()) {
				send(failure);
				return;
			}
			if (isSentryLoading()) {
				held.push(failure);
			}
		}

		function flush() {
			held.splice(0).forEach(send);
		}

		// The loader's stubs include captureMessage but not getClient, and the SDK
		// drops captures made after its bundle runs but before init creates a client.
		function isSentryReady() {
			return Boolean(window.Sentry?.getClient?.());
		}

		function isLoaderQueueing() {
			return typeof window.Sentry?.captureMessage === 'function' && typeof window.Sentry.getClient !== 'function';
		}

		// 3p-sentry.liquid defines sentryOnLoad before the async loader, and the
		// loader clears it once the SDK has run it, so a function here means Sentry
		// is configured on this brand but has not arrived yet. Brands without a
		// Sentry id never define it, so nothing is held for them.
		function isSentryLoading() {
			return typeof window.sentryOnLoad === 'function';
		}

		function send(failure) {
			window.Sentry.captureMessage(failure.message, {
				level: failure.level,
				fingerprint: ['uft-failure', failure.fingerprint],
				tags: { uftSubsystem: failure.fingerprint },
				extra: failure.extra,
			});
		}

		function logToConsole(failure) {
			const log = failure.level === 'warning' ? console.warn : console.error;
			log(failure.message, failure.extra);
		}

		return { report, flush };
	})();
